//src/production/components/ProductionPlan.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Collapse,
  Drawer,
  Box,
  Divider,
  TextField,
  InputAdornment,
  IconButton
} from "@mui/material";
import axios from 'axios';
import { Search as SearchIcon } from "@mui/icons-material"; // Import the Search icon
import env from "react-dotenv";
import CreateProductionPlan from "./CreateProductionPlan"; // Adjust the path as needed
import { saveAs } from 'file-saver';
import {
  createWorkbook,
  createWorksheet,
  addTitleRow,
  addSummaryRow,
  addHeaders,
  applyCellStyles,
  setColumnWidths
} from '../../utils/excelStyles'; // Import the Excel styling functions
import CustomSelect from '../../utils/CustomSelect'; // Import the CustomSelect component
import FilterListIcon from '@mui/icons-material/FilterList';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import RefreshIcon from '@mui/icons-material/Refresh';

const ProductionPlan = () => {
  const user = useSelector((state) => state.user);
  // const isAdmin = user?.role === 'Admin'; // Determine if the user is an Admin
  const [isAdmin, setIsAdmin] = useState(() => {
      if (!user || !user.department) return false; // Handle case where user or department is undefined
      const role = user.role?.toLowerCase();
      const department = user.department?.toLowerCase();
      // Check if user is Admin and department does not include Store or Supply or Others
      const isAdminRole = role === 'admin' && !department.includes('store') && !department.includes('supply') && !department.includes('others');
      
      // Check if user is Super Admin
      const isSuperAdminRole = role === 'super admin';
      return isAdminRole || isSuperAdminRole;
    });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!location.key || location.key === 'default') {
      navigate("/");
    }
  }, [location, navigate]);

  const [plans, setPlans] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [viewType, setViewType] = useState(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [product_id, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [plan_id, setPlanId] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [totalAcceptedQuantities, setTotalAcceptedQuantities] = useState({});
  const [totalRejectedQuantities, setTotalRejectedQuantities] = useState({});
  const [latestQuantities, setLatestQuantities] = useState({});
  const [totalInventoryQuantities, setTotalInventoryQuantities] = useState({});
  //new states for advance filter
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [uniqueYears, setUniqueYears] = useState([]);
  const [uniqueMonths, setUniqueMonths] = useState([]);
  const [uniqueProducts, setUniqueProducts] = useState([]);

  // States to control dropdown opens separately
  const [openYearsDropdown, setOpenYearsDropdown] = useState(false);
  const [openMonthsDropdown, setOpenMonthsDropdown] = useState(false);
  const [openProductsDropdown, setOpenProductsDropdown] = useState(false);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  // useEffect(() => {
  //   const fetchProductionPlans = async () => {
  //     try {
  //       const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-plan`);
  //       const sortedPlans = response.data.sort((a, b) => b.plan_id - a.plan_id); // Sort by plan_id descending
  //       // console.log("Sorted production plans:",sortedPlans); // Log the response data
  //       setPlans(sortedPlans);
  //     } catch (error) {
  //       console.error("Error fetching production plans:", error);
  //     }
  //   };

  //   fetchProductionPlans();
  // }, []);

    const fetchProductionPlans = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-plan`);
        const sortedPlans = response.data.sort((a, b) => b.plan_id - a.plan_id); // Sort by plan_id descending
        // console.log("Sorted production plans:",sortedPlans); // Log the response data
        setPlans(sortedPlans);

      // Fetch total accepted, rejected quantities, latest quantities, and inventory quantities for each plan
      const acceptedQuantities = {};
      const rejectedQuantities = {};
      const latestQuantities = {};
      const inventoryQuantities = {};
      for (const plan of sortedPlans) {
        const acceptedQuantity = await fetchTotalAcceptedQuantity(plan.plan_id);
        acceptedQuantities[plan.plan_id] = acceptedQuantity; // Store accepted quantity
        const rejectedQuantity = await fetchTotalRejectedQuantity(plan.plan_id);
        rejectedQuantities[plan.plan_id] = rejectedQuantity; // Store rejected quantity
        const latestQuantity = await fetchLatestQuantity(plan.plan_id);
        latestQuantities[plan.plan_id] = latestQuantity; // Store latest quantity
        const inventoryQuantity = await fetchTotalInventoryQuantity(plan.product_id);
        inventoryQuantities[plan.product_id] = inventoryQuantity;  // Store inventory quantity
      }
      setTotalAcceptedQuantities(acceptedQuantities); // Update state with accepted quantities
      setTotalRejectedQuantities(rejectedQuantities); // Update state with rejected quantities
      setLatestQuantities(latestQuantities); // Update state with latest quantities
      setTotalInventoryQuantities(inventoryQuantities); // Update state with inventory quantities

      } catch (error) {
        console.error("Error fetching production plans:", error);
      }
    };

  useEffect(() => {
    fetchProductionPlans();
  }, []);

  // Update the useEffect to fetch unique values for new filters
useEffect(() => {
  const fetchUniqueValues = () => {
    const years = [...new Set(plans.map(plan => plan.production_year))];
    const months = [...new Set(plans.map(plan => plan.production_month))];
    const products = [...new Set(plans.map(plan => plan.product_name))];
    setUniqueYears(years);
    setUniqueMonths(months);
    setUniqueProducts(products);
  };
  fetchUniqueValues();
}, [plans]);

// Update the handleSearch function to include new filters
// const handleSearch = (e) => {
//   const value = e.target.value.toLowerCase();
//   setSearchQuery(value);
//   filterPlans(value, selectedYears, selectedMonths, selectedProducts);
// };

const handleSearch = (e) => {
  const value = e.target.value.toLowerCase();
  setSearchQuery(value);

  // If backspace is pressed and the search query is empty, reload the data
  if (e.key === "Backspace" && value === "") {
    fetchProductionPlans(); // Reload the data
  } else {
    filterPlans(value, selectedYears, selectedMonths, selectedProducts);
  }
};

// Update the filterPlans function to include new filters
const filterPlans = (searchQuery, selectedYears, selectedMonths, selectedProducts) => {
  const filtered = plans.filter((plan) => {
    const matchesSearch = Object.values(plan).some(field => 
      field && field.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
    const matchesYear = selectedYears.length === 0 || selectedYears.includes(plan.production_year);
    const matchesMonth = selectedMonths.length === 0 || selectedMonths.includes(plan.production_month);
    const matchesProduct = selectedProducts.length === 0 || selectedProducts.includes(plan.product_name);
    return matchesSearch && matchesYear && matchesMonth && matchesProduct;
  });
  setPlans(filtered);
};

  const handleExport = async () => {
    const workbook = createWorkbook();
    const worksheet = createWorksheet(workbook, 'Production Plans');
    addTitleRow(worksheet, 'Production Plans List');
    addSummaryRow(worksheet, 'Summary of Production Plans');
    // Add header rows
    const headers = ['Year', 'Month', 'Product', 'Planned Quantity', 'Production till date', 'Inventory', 'Dispatched', 'In Process', 'Rejection'];
    addHeaders(worksheet, headers);
    // Add data rows
    plans.forEach(plan => {
      worksheet.addRow([
        plan.production_year,
        plan.production_month,
        plan.product_name,
        plan.planned_quantity,
        totalAcceptedQuantities[plan.plan_id] || 0,
        totalInventoryQuantities[plan.product_id] || 0,
        plan.dispatched,
        latestQuantities[plan.plan_id] || 0,
        totalRejectedQuantities[plan.plan_id] || 0
      ]);
    });
    applyCellStyles(worksheet);
    setColumnWidths(worksheet, [10, 20, 20, 20, 20, 20, 20, 20, 20]); // Set column widths
    // Save the workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, 'ProductionPlansData.xlsx');
  };
  
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedYears([]);
    setSelectedMonths([]);
    setSelectedProducts([]);
    setPlans(plans); // Reset filtered plans to all plans
  };

// Function to fetch total accepted quantity
const fetchTotalAcceptedQuantity = async (planId) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-plan/total-accepted-quantity/${planId}`);
    return response.data.totalAcceptedQuantity; // Return the quantity
  } catch (error) {
    console.error("Error fetching total accepted quantity:", error);
    return 0; // Return 0 in case of error
  }
};

// Function to fetch total rejected quantity
const fetchTotalRejectedQuantity = async (planId) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-plan/total-rejected-quantity/${planId}`);
    return response.data.totalRejectedQuantity; // Return the quantity
  } catch (error) {
    console.error("Error fetching total rejected quantity:", error);
    return 0; // Return 0 in case of error
  }
};

// Function to fetch latest quantity (unit of current jc)
const fetchLatestQuantity = async (planId) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-plan/latest-quantity/${planId}`);
    return response.data.latestQuantity; // Return the quantity
  } catch (error) {
    console.error("Error fetching latest quantity:", error);
    return 0; // Return 0 in case of error
  }
};

// Function to fetch total inventory quantity
const fetchTotalInventoryQuantity = async (productId) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-plan/total-inventory-quantity/${productId}`);
    return response.data.totalQuantity; // Return the quantity
  } catch (error) {
    console.error("Error fetching total inventory quantity:", error);
    return 0; // Return 0 in case of error
  }
};

  const toggleExpand = (index, type = null) => {
    if (expandedRow === index && viewType === type) {
      setExpandedRow(null);
      setViewType(null);
    } else {
      setExpandedRow(index);
      setViewType(type);
    }
  };

  const openDrawer = (product_id, productName, plan_id) => {
    setProductId(product_id);
    setProductName(productName);
    setPlanId(plan_id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    window.location.reload();
  };

  const handleSave = async () => {
    const assigned_operator_id = user?.id;

    if (!product_id || !quantity) {
      alert("Please ensure all fields are filled!");
      return;
    }

    const payload = {
      product_id,
      assigned_operator_id,
      quantity,
      plan_id,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/job-cards/create`, payload);
      if (response.status === 201) {
        // alert("Job card created successfully!");
        // closeDrawer();
        navigate(`/view-job-card/${plan_id}`)
      }
    } catch (error) {
      console.error("Error creating job card:", error);
      alert("Failed to create job card. Please try again.");
    }
  };

  const handleSaveProductionPlan = (payload) => {
    // console.log("Saved Production Plan Data:", payload);

    fetch(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-plan`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
    .then((response) => response.json())
    .then((data) => {
      // console.log('Production Plan Saved:', data);
      alert('Production Plan saved successfully!');
    })
    .catch((error) => {
      console.error('Error:', error);
    });
  };

  const formatDate = (dateStr) => {
  if (!dateStr) return ''; // Handles null, undefined, or empty string
  const date = new Date(dateStr);
  return isNaN(date) ? '' : date.toLocaleDateString('en-GB'); // Handles invalid dates
};

  return (
      <Box sx={{
          height: 670,
          width: '85%',
          marginLeft:"210px",
          marginRight:"150px",

        }} m={2}>
      <Box display="flex" alignItems="center" mb={2}>
        {isAdmin && (
          <Button
            sx={{
              backgroundColor: '#00308F',
              color: '#ffffff',
              padding: '2px 4px',
              '&:hover': {
                backgroundColor: '#00207E',
              },
            }}
            variant="contained"
            onClick={handleOpenModal}
          >
            Create
          </Button>
        )}
      
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearch}
          onKeyDown={handleSearch} // Add this line
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ 
                    width: '970px', // Set the desired width here

                    marginLeft: "10px",
                    marginRight: "10px",
                    backgroundColor: '#ffffff',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#00308F',
                      },
                      '&:hover fieldset': {
                        borderColor: '#00207E',
                      },
                    },
                  }}
        />

        <Button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          startIcon={<FilterListIcon />}
        >
          Filter
        </Button>

        <Button 
          onClick={handleExport}
          startIcon={<SaveAltIcon />}
        >
          Export
        </Button>
        
        <IconButton
          onClick={() => window.location.reload()} // Refresh the page
          color="primary"
          sx={{
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
          }}
        >
          <RefreshIcon />
        </IconButton>

      </Box>

{showAdvancedFilters && (
  <Box sx={{ marginTop: "15px", marginBottom: "10px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
    <CustomSelect
      label="Year"
      value={selectedYears}
      onChange={(e) => setSelectedYears(e.target.value)}
      options={uniqueYears}
      open={openYearsDropdown}
      setOpen={setOpenYearsDropdown}
      getOptionLabel={(option) => option} // Adjust based on your object structure
    />
    <CustomSelect
      label="Month"
      value={selectedMonths}
      onChange={(e) => setSelectedMonths(e.target.value)}
      options={uniqueMonths}
      open={openMonthsDropdown}
      setOpen={setOpenMonthsDropdown}
      getOptionLabel={(option) => option} // Adjust based on your object structure
    />
    <CustomSelect
      label="Product"
      value={selectedProducts}
      onChange={(e) => setSelectedProducts(e.target.value)}
      options={uniqueProducts}
      open={openProductsDropdown}
      setOpen={setOpenProductsDropdown}
      getOptionLabel={(option) => option} // Adjust based on your object structure
    />
    <Button onClick={clearFilters}>Clear Filter</Button>
  </Box>
)}

      <TableContainer component={Paper} style={{ maxHeight: '600px', overflowY: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Year</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Month</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Product</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Planned Quantity</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Machine Details</TableCell>
              <TableCell align="center" style={{ fontWeight : "bold" }}>Consignee Details</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Production till date</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Inventory</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Dispatched</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>In Process</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Rejection</TableCell>
              {isAdmin && (<TableCell align="center" style={{ fontWeight: "bold" }}>Total Prod Cost</TableCell>)}
              <TableCell align="center" style={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan, index) => (
              <React.Fragment key={plan.plan_id}>
                <TableRow>
                  <TableCell align="center">{plan.production_year}</TableCell>
                  <TableCell align="center">{plan.production_month}</TableCell>
                  <TableCell align="center">{plan.product_name}</TableCell>
                  <TableCell align="center">{plan.planned_quantity}</TableCell>
                  <TableCell align="center">
                    <Button color="secondary" size="small" onClick={() => toggleExpand(index, "machine")}>
                      Machines
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Button color="warning" size="small" onClick={() => toggleExpand(index, "consignee")}>
                      Consignees
                    </Button>
                  </TableCell>
                  {/* <TableCell align="center">{plan.production_till_date}</TableCell> */}
                  <TableCell align="center">{totalAcceptedQuantities[plan.plan_id] || 0}</TableCell>
                  {/* <TableCell align="center">{plan.inventory}</TableCell> */}
                  <TableCell align="center">{totalInventoryQuantities[plan.product_id] || 0}</TableCell>
                  <TableCell align="center">{plan.dispatched}</TableCell>
                  {/* <TableCell align="center">{plan.inprocess}</TableCell> */}
                  <TableCell align="center">{latestQuantities[plan.plan_id] || 0}</TableCell>
                  {/* <TableCell align="center">{plan.rejection}</TableCell> */}
                  <TableCell align="center">{totalRejectedQuantities[plan.plan_id] || 0}</TableCell>
                  {isAdmin && (<TableCell align="center">
                     <Button sx={{ 
                              color: 'blue', // Change text color to blue
                              cursor: 'pointer', // Change cursor to pointer to indicate it's clickable
                              '&:hover': {
                                backgroundColor: '#ADD8E6', // Change background color to light blue on hover
                              }
                            }}  
                      onClick={() => toggleExpand(index, "productionCost")}>
                      {plan.total_production_cost || 0}
                    </Button>
                  </TableCell>)}
                  <TableCell align="center">
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                      {isAdmin && (
                        <Button color="success" onClick={() => openDrawer(plan.product_id, plan.product_name, plan.plan_id)}>
                          Create Job Card
                        </Button>
                      )}
                      <Button color="secondary" onClick={() => navigate(`/view-job-card/${plan.plan_id}`)}>
                        View
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {/* Expandable Production Cost Details */}
                <TableRow>
                  <TableCell colSpan={12} style={{ paddingBottom: 0, paddingTop: 0 }}>
                    <Collapse in={expandedRow === index && viewType === "productionCost"} timeout="auto" unmountOnExit>
                      <Box margin={2}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }} gutterBottom component="div">
                          Cost Details
                        </Typography>
                        <Table size="small" aria-label="production-cost-details">
                          <TableHead>
                            <TableRow>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Direct Cost</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Indirect Cost</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Total Production Cost</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell align="center">{plan.direct_cost || 0}</TableCell>
                              <TableCell align="center">{plan.indirect_cost || 0}</TableCell>
                              <TableCell align="center">{plan.total_production_cost || 0}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
                {/* Expandable Machine Details */}
                <TableRow>
                  <TableCell colSpan={12} style={{ paddingBottom: 0, paddingTop: 0 }}>
                    <Collapse in={expandedRow === index && viewType === "machine"} timeout="auto" unmountOnExit>
                      <Box margin={2}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }} gutterBottom component="div">
                          Machine Details
                        </Typography>
                        <Table size="small" aria-label="machine-details">
                          <TableHead>
                            <TableRow>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Machine</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Start Date</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>End Date</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Quantity</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Indirect Cost</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {plan.machines.map((machine, idx) => (
                              <TableRow key={idx}>
                                <TableCell align="center">{machine.machine}</TableCell>
                                {/* <TableCell align="center">{new Date(machine.start_date).toLocaleDateString()}</TableCell>
                                <TableCell align="center">{new Date(machine.end_date).toLocaleDateString()}</TableCell> */}
                                <TableCell align="center">{formatDate(machine.start_date)}</TableCell>
                                <TableCell align="center">{formatDate(machine.end_date)}</TableCell>
                                <TableCell align="center">{machine.made_quantity}</TableCell>
                                <TableCell align="center">{machine.inCost || 0}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
                {/* Expandable Consignee Details */}
                <TableRow>
                  <TableCell colSpan={12} style={{ paddingBottom: 0, paddingTop: 0 }}>
                    <Collapse in={expandedRow === index && viewType === "consignee"} timeout="auto" unmountOnExit>
                      <Box margin={2}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }} gutterBottom component="div">
                          Consignee Details
                        </Typography>
                        <Table size="small" aria-label="consignee-details">
                          <TableHead>
                            <TableRow>
                              <TableCell align="center" style={{ fontWeight : 'bold' }}>Consignee Name</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Order/PO No.</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Quantity</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Delivery Date</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Dispatch Date</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {plan.consignees.map((consignee, idx) => (
                              <TableRow key={idx}>
                                <TableCell align="center">{consignee.consignee_name}</TableCell>
                                <TableCell align="center">{consignee.order_PON}</TableCell>
                                <TableCell align="center">{consignee.consignee_quantity}</TableCell>
                                {/* <TableCell align="center">{new Date(consignee.delivery_date).toLocaleDateString()}</TableCell>
                                <TableCell align="center">{new Date(consignee.dispatch_date).toLocaleDateString()}</TableCell> */}
                                <TableCell align="center">{formatDate(consignee.delivery_date)}</TableCell>
                                <TableCell align="center">{formatDate(consignee.dispatch_date)}</TableCell>
                                <TableCell align="center">{consignee.consignee_status}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={closeDrawer}
        PaperProps={{ style: { width: "30%" } }}
      >
        <Box p={2}>
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            Create Job Card
          </Typography>
          <Divider style={{ marginBottom: "20px" }} />
          <TextField
            label="Product Name"
            variant="outlined"
            fullWidth
            value={productName}
            disabled
            style={{ marginBottom: "20px" }}
          />
          <TextField
            label="Enter Quantity"
            variant="outlined"
            fullWidth
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            style={{ marginBottom: "20px" }}
          />
          <Box display="flex" justifyContent="space-between">
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#714b67",
                "&:hover": {
                  backgroundColor: "#604058",
                },
              }}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              variant="contained"
              sx={{
                color: "#111827",
                backgroundColor: "#f9f9f9",
                "&:hover": {
                  backgroundColor: "#fafafa" },
              }}
              onClick={closeDrawer}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>

      <CreateProductionPlan
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveProductionPlan}
      />
    </Box>
  );
};

export default ProductionPlan;